import React, { FC, ReactNode, useState, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import './popup.scss';

interface PopupProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
}

export const PopupContext = React.createContext<{ onClose: () => void }>({
    onClose: () => {},
});

const Popup: FC<PopupProps> = ({ isOpen, onClose, children }) => {
    const [mounted, setMounted] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [animate, setAnimate] = useState(false);

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        if (mounted && isOpen) setShouldRender(true);
    }, [mounted, isOpen]);

    useEffect(() => {
        if (shouldRender) requestAnimationFrame(() => setAnimate(true));
    }, [shouldRender]);

    useEffect(() => {
        if (!isOpen && shouldRender) {
            setAnimate(false);
            const timer = setTimeout(() => setShouldRender(false), 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    useLayoutEffect(() => {
        if (!mounted || !isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollBarWidth = window.innerWidth - html.clientWidth;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (scrollBarWidth) body.style.paddingRight = `${scrollBarWidth}px`;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => {
            html.style.overflow = '';
            body.style.overflow = '';
            body.style.paddingRight = '';
            document.removeEventListener('keydown', handleKey);
        };
    }, [mounted, isOpen, onClose]);

    if (!mounted || !shouldRender) return null;

    return ReactDOM.createPortal(
        <PopupContext.Provider value={{ onClose }}>
            <div className={`popup${animate ? ' popup--open' : ''}`}>
                <div className="popup__overlay" onClick={onClose} aria-hidden="true" />
                <div className="popup__content" role="dialog" aria-modal="true">
                    <button className="popup__close" onClick={onClose} type="button" aria-label="Закрыть">
                        <img className="popup__close_icon" src="/icons/close.svg" alt="Закрыть" />
                    </button>
                    <div className="popup__body">{children}</div>
                </div>
            </div>
        </PopupContext.Provider>,
        document.body,
    );
};

export default Popup;
